/**
 * HrPlanningPrint — impression du rapport People « Personnel planning ».
 *
 * Reprend la projection de HrPlanningPage (baseline contrats + overlay des
 * mouvements hypothétiques : positions ouvertes, départs planifiés, changements
 * de taux), en lecture seule, au gabarit A4 paysage commun aux rapports print.
 * Aucun contrôle d'édition : c'est un instantané du prévisionnel de l'année.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  listHrContracts, listHrPeople, listHrLppRates, peopleCostSynthesis,
  listPayrollMovements,
} from '../../../api/hr';
import { formatNumber } from '../../../utils/format';
import { cellFteStyle, fteColor } from '../fteColor';
import { monthlyMassSeries, addExtraSalariesToCells, extraMonths } from '../salaryMass';
import PrintReportHeader from './PrintReportHeader';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ALL_M = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/* ── Helpers (identiques à HrPlanningPage) ───────────────────────────────── */
function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function monthStartOf(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function fmtMonthYear(s) {
  if (!s) return '—';
  const d = parseLocalDate(s);
  return d ? `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}` : '—';
}
function contractActiveInMonth(c, year, m) {
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  const monthStart = new Date(year, m, 1);
  const monthEnd   = new Date(year, m + 1, 0);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}
function pickReferenceContract(contracts) {
  if (!contracts || contracts.length === 0) return null;
  const open = contracts.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : contracts;
  return pool.reduce((b, c) => (!b || (c.start_date || '') > (b.start_date || '') ? c : b), null);
}
function pickContractForMonth(contracts, year, m) {
  const active = contracts.filter(c => contractActiveInMonth(c, year, m));
  if (active.length === 0) return null;
  const open = active.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : active;
  return pool.reduce((b, c) => (!b || (c.start_date || '') > (b.start_date || '') ? c : b), null);
}
function buildBaseCells(person, contracts, year) {
  const entry = person.entry_date ? parseLocalDate(person.entry_date) : null;
  const term  = person.termination_date ? parseLocalDate(person.termination_date) : null;
  return ALL_M.map(m => {
    const monthStart = new Date(year, m, 1);
    const monthEnd   = new Date(year, m + 1, 0);
    if (entry && entry > monthEnd)  return { kind: 'before', pct: 0 };
    if (term  && term  < monthStart) return { kind: 'after',  pct: 0 };
    const c = pickContractForMonth(contracts, year, m);
    if (!c) return { kind: 'none', pct: 0 };
    return { kind: 'active', pct: Math.round(Number(c.activity_rate || 0) * 100) };
  });
}
function applyPersonMovements(baseCells, salary100, personMovements, year, isOpen) {
  return ALL_M.map(m => {
    const base = baseCells[m];
    let kind = base.kind;
    let rate = base.kind === 'active' ? base.pct / 100 : 0;
    const cell = new Date(year, m, 1);
    if (base.kind === 'active' && isOpen(m)) {
      for (const mv of personMovements) {
        if (mv.movement_type !== 'rate_change') continue;
        const sd = monthStartOf(parseLocalDate(mv.start_month));
        const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
        if (cell >= sd && (!ed || cell <= ed)) { rate = Number(mv.fte || 0); kind = 'rate'; }
      }
      for (const mv of personMovements) {
        if (mv.movement_type !== 'departure') continue;
        const sd = monthStartOf(parseLocalDate(mv.start_month));
        if (cell >= sd) { rate = 0; kind = 'exit'; }
      }
    }
    return { kind, pct: Math.round(rate * 100), gross: salary100 * rate };
  });
}
function buildSyntheticRow(mv, year, isOpen) {
  const sign = mv.movement_type === 'departure' ? -1 : 1;
  const fte = Number(mv.fte != null ? mv.fte : 1) || 0;
  const amount = Number(mv.monthly_amount || 0);
  const salary100 = fte > 0 ? amount / fte : amount;
  const cells = ALL_M.map(m => {
    const cell = new Date(year, m, 1);
    const sd = monthStartOf(parseLocalDate(mv.start_month));
    const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
    const active = isOpen(m) && cell >= sd && (!ed || cell <= ed);
    return {
      kind: sign > 0 ? 'hire' : 'cut',
      pct: active ? Math.round(fte * 100) * sign : 0,
      gross: active ? amount * sign : 0,
    };
  });
  return { id: mv.id, synthetic: true, mv, sign, label: mv.label, salary100, cells };
}

const fmtChf = (n) => formatNumber(Math.round(Number(n) || 0), 0);

// Style d'une cellule de grille au print, par kind (cohérent avec la page).
function planCellStyle(cell) {
  if (cell.kind === 'active' || cell.kind === 'rate' || cell.kind === 'hire') return fteColor(cell.pct);
  if (cell.kind === 'cut' || cell.kind === 'exit') return { background: 'var(--terracotta-light)', color: 'var(--terracotta-dark)' };
  return cellFteStyle(cell.kind, cell.pct);
}

export default function HrPlanningPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const months = useMemo(() => extraMonths(activeTenant),
    [activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month]);
  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [lppRates, setLppRates] = useState([]);
  const [synth, setSynth] = useState(null);
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    if (!tenantId) { onDataReady && onDataReady('personnel-planning'); return; }
    Promise.all([
      listHrContracts(tenantId),
      listHrPeople(tenantId),
      listHrLppRates(tenantId),
      peopleCostSynthesis(tenantId, year, 'all'),
      listPayrollMovements(tenantId),
    ]).then(([c, p, l, s, m]) => {
      if (!c.error) setContracts(c.data || []);
      if (!p.error) setPeople(p.data || []);
      if (!l.error) setLppRates(l.data || []);
      if (!s.error) setSynth(s.data || s);
      if (!m.error) setMovements(m.data || []);
      onDataReady && onDataReady('personnel-planning');
    }).catch(() => { onDataReady && onDataReady('personnel-planning'); });
  }, [tenantId, year]); // eslint-disable-line

  // Borne réel/forecast (identique à la page).
  const closedThrough = activeTenant?.last_closed_period ? parseLocalDate(activeTenant.last_closed_period) : null;
  const firstOpen = closedThrough ? new Date(closedThrough.getFullYear(), closedThrough.getMonth() + 1, 1) : null;
  const isOpenMonth = (m) => !firstOpen || new Date(year, m, 1) >= firstOpen;

  const csEmprRate = Number(synth?.cs_fallback_rate_empr || 0);
  const lppEmprPct = useMemo(() => {
    if (!lppRates.length) return 0;
    const sumRef = lppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
    const sumEmpr = lppRates.reduce((a, r) => a + Number(r.lpp_employer_chf || 0), 0);
    return sumRef > 0 ? (sumEmpr / sumRef) * 100 : 0;
  }, [lppRates]);
  const lppEmplPct = useMemo(() => {
    if (!lppRates.length) return 0;
    const sumRef = lppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
    const sumEmpl = lppRates.reduce((a, r) => a + Number(r.lpp_employee_chf || 0), 0);
    return sumRef > 0 ? (sumEmpl / sumRef) * 100 : 0;
  }, [lppRates]);
  const totalEmprRate = csEmprRate + lppEmprPct;

  const movementsByPerson = useMemo(() => {
    const m = new Map();
    for (const mv of movements) {
      if (!mv.person_id) continue;
      if (!m.has(mv.person_id)) m.set(mv.person_id, []);
      m.get(mv.person_id).push(mv);
    }
    return m;
  }, [movements]);

  const peopleRows = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd   = new Date(year, 11, 31);
    const byPerson = new Map();
    for (const c of contracts) {
      if (!c.person_id) continue;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    }
    return people
      .filter(p => {
        if (p.entry_date && parseLocalDate(p.entry_date) > yearEnd) return false;
        if (p.termination_date && parseLocalDate(p.termination_date) < yearStart) return false;
        return true;
      })
      .map(p => {
        const pc = byPerson.get(p.id) || [];
        const ref = pickReferenceContract(pc);
        const ar = ref ? Number(ref.activity_rate || 0) : 0;
        const ms = ref ? Number(ref.monthly_salary || 0) : 0;
        const salary100 = (ref && ar > 0) ? ms / ar : 0;
        const base = buildBaseCells(p, pc, year);
        // Salaires extra (13e, 14e) versés sur leur mois (settings tenant), pas étalés.
        const cells = addExtraSalariesToCells(
          applyPersonMovements(base, salary100, movementsByPerson.get(p.id) || [], year, isOpenMonth),
          ref, months,
        );
        return { ...p, synthetic: false, department: ref?.department || '', salary100, curRate: ar, cells, hasMv: (movementsByPerson.get(p.id) || []).length > 0 };
      })
      .sort((a, b) => (b.entry_date || '').localeCompare(a.entry_date || ''));
  }, [people, contracts, year, movementsByPerson, months, activeTenant?.last_closed_period]); // eslint-disable-line

  const syntheticRows = useMemo(() =>
    movements
      .filter(mv => !mv.person_id)
      .map(mv => buildSyntheticRow(mv, year, isOpenMonth))
      .filter(r => r.cells.some(c => c.gross !== 0)),
    [movements, year, activeTenant?.last_closed_period]); // eslint-disable-line

  const allRows = useMemo(() => [...peopleRows, ...syntheticRows], [peopleRows, syntheticRows]);

  // Masse mensuelle (base + salaires extra positionnés sur leur mois de versement).
  const monthlyBase = useMemo(() =>
    monthlyMassSeries(contracts, year, months),
    [contracts, year, months]);
  const monthlyEff = useMemo(() => ALL_M.map(m => allRows.reduce((a, r) => a + (r.cells[m].gross || 0), 0)), [allRows]);
  const monthlyFte = useMemo(() => ALL_M.map(m => allRows.reduce((a, r) => a + (r.cells[m].pct || 0) / 100, 0)), [allRows]);
  const monthlyHc = useMemo(() => ALL_M.map(m => allRows.reduce((a, r) => {
    const pct = r.cells[m].pct || 0;
    return a + (pct > 0 ? 1 : pct < 0 ? -1 : 0);
  }, 0)), [allRows]);

  const annualBase = monthlyBase.reduce((a, n) => a + n, 0);
  const annualEff  = monthlyEff.reduce((a, n) => a + n, 0);
  const impact     = annualEff - annualBase;
  const avgMonthly = annualEff / 12;
  const totalCost  = annualEff + (annualEff * totalEmprRate / 100);
  const avgFte     = monthlyFte.reduce((a, n) => a + n, 0) / 12;

  const lppByPerson = useMemo(() => {
    const m = {};
    for (const r of lppRates) m[r.person_id] = (Number(r.lpp_employer_chf) || 0) + (Number(r.lpp_employee_chf) || 0);
    return m;
  }, [lppRates]);
  const lppExact = peopleRows.reduce((a, p) => a + (lppByPerson[p.id] || 0), 0);
  const hiresAnnualGross = syntheticRows.filter(r => r.sign > 0)
    .reduce((a, r) => a + r.cells.reduce((s, c) => s + Math.max(0, c.gross), 0), 0);
  const lppEstimated = hiresAnnualGross * (lppEmprPct + lppEmplPct) / 100;
  const lppTotal = lppExact + lppEstimated;

  const yearSuffix = String(year).slice(-2);
  const FR_MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const maxBar = Math.max(...monthlyEff, ...monthlyBase, 1);

  // Liste des mouvements (lecture seule) — nominatifs d'abord, puis globaux.
  const nameById = new Map(people.map(p => [p.id, `${(p.last_name || '').toUpperCase()} ${p.first_name || ''}`.trim()]));
  const moveLabel = (mv) => (
    mv.movement_type === 'hire' ? 'Position ouverte'
      : mv.movement_type === 'rate_change' ? p('Changement de taux')
        : mv.person_id ? p('Départ planifié') : p('Réduction générale')
  );
  const sortedMovements = [...movements].sort((a, b) => {
    const an = a.person_id ? 0 : 1; const bn = b.person_id ? 0 : 1;
    if (an !== bn) return an - bn;
    return String(a.start_month || '').localeCompare(String(b.start_month || ''));
  });

  return (
    <>
    {/* ── Page 1 : synthèse + mouvements ─────────────────────────────── */}
    <section className="ppl-print__section">
      <PrintReportHeader category="People" title={t('phrases:p_fdb24f2ea7')} year={year} tenant={activeTenant} editedAt={editedAt} />

      <div className="ppl-print__kpis">
        <Kpi label={p('Salaires bruts')} value={`${fmtChf(annualEff)} CHF`} hint={impact !== 0 ? `mouvements ${impact > 0 ? '+' : ''}${fmtChf(impact)}` : 'baseline contrats'} />
        <Kpi label={p('Moyenne mensuelle')} value={`${fmtChf(avgMonthly)} CHF`} />
        <Kpi label={t('phrases:p_4ff08f6137')} value={`${fmtChf(totalCost)} CHF`} hint={`+${formatNumber(totalEmprRate, 1)}% (CS ${formatNumber(csEmprRate, 1)} + LPP ${formatNumber(lppEmprPct, 1)})`} accent="primary" />
        <Kpi label="LPP (exacte + est.)" value={`${fmtChf(lppTotal)} CHF`} hint={lppEstimated > 0 ? `dont est. ${fmtChf(lppEstimated)}` : p('contrats — exacte')} />
        <Kpi label={p('FTE moyen annuel')} value={formatNumber(avgFte, 1)} accent="sage" />
        <Kpi label={p('Δ vs baseline')} value={`${impact >= 0 ? '+' : ''}${fmtChf(impact)} CHF`} hint={annualBase ? `${impact >= 0 ? '+' : ''}${formatNumber(impact / annualBase * 100, 1)}%` : '—'} />
      </div>

      <div className="ppl-print__planning-chart">
        <div className="hr-chart__title">VARIATION DES SALAIRES BRUTS · {year}
          <span style={{ marginLeft: 10, color: 'var(--color-text-muted)', fontSize: 9, textTransform: 'none', letterSpacing: 0 }}>effectif (contrats + mouvements)</span>
        </div>
        <div className="hr-chart__bars" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: 6 }}>
          {monthlyEff.map((v, i) => {
            const h = Math.max(2, (v / maxBar) * 100);
            const delta = v - monthlyBase[i];
            const showDelta = Math.round(delta / 1000) !== 0;
            return (
              <div className="hr-chart__col" key={i}>
                <div className="hr-chart__val">
                  {formatNumber(v / 1000, 0)}k
                  {showDelta && (
                    <span className={`hr-chart__val-delta ${delta > 0 ? 'is-up' : 'is-down'}`}>
                      {delta > 0 ? '+' : ''}{formatNumber(delta / 1000, 0)}k
                    </span>
                  )}
                </div>
                <div className="hr-chart__bar-wrap">
                  <div className="hr-chart__bar" style={{ height: `${h}%`, background: 'var(--color-accent)' }} />
                </div>
                <div className="hr-chart__month">{FR_MONTHS[i]}</div>
              </div>
            );
          })}
        </div>
      </div>

      <table className="ppl-print__table ppl-print__table--total-once">
        <caption>Mouvements planifiés · {sortedMovements.length}</caption>
        <thead>
          <tr>
            <th>{p('Type')}</th>
            <th>{p('Personne / libellé')}</th>
            <th className="nowrap">De</th>
            <th className="nowrap">À</th>
            <th className="num nowrap">Taux %</th>
            <th className="num nowrap">{p('CHF / mois')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedMovements.length === 0 ? (
            <tr><td colSpan={6} style={{ color: '#777' }}>{p('Aucun mouvement planifié.')}</td></tr>
          ) : sortedMovements.map((mv, idx) => (
            <tr key={mv.id} className={idx % 2 === 0 ? 'zebra' : ''}>
              <td>{moveLabel(mv)}</td>
              <td>{mv.person_id ? (nameById.get(mv.person_id) || '—') : (mv.label || '—')}</td>
              <td className="nowrap">{fmtMonthYear(mv.start_month)}</td>
              <td className="nowrap">{fmtMonthYear(mv.end_month)}</td>
              <td className="num nowrap">{mv.fte != null ? `${Math.round(Number(mv.fte) * 100)}%` : '—'}</td>
              <td className="num nowrap">{mv.monthly_amount ? fmtChf(mv.monthly_amount) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>

    {/* ── Page 2 : évolution du personnel (grille) ───────────────────── */}
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="People" title={t('phrases:p_ee273ce260')} year={year} tenant={activeTenant} editedAt={editedAt} />

      <table className="ppl-print__table ppl-print__table--fixed ppl-print__table--dense ppl-print__table--total-once">
        <caption>Grille prévisionnelle · {peopleRows.length} personnes + {syntheticRows.length} mouvement{syntheticRows.length === 1 ? '' : 's'} · taux d'activité mensuel (contrats + overlay)</caption>
        <colgroup>
          <col style={{ width: '40mm' }} />
          <col style={{ width: '18mm' }} />
          <col style={{ width: '18mm' }} />
          <col style={{ width: '16mm' }} />
          <col style={{ width: '16mm' }} />
          {ALL_M.map(i => <col key={i} style={{ width: '12.8mm' }} />)}
        </colgroup>
        <thead>
          <tr>
            <th>Name</th>
            <th>Dept</th>
            <th className="num nowrap">100% CHF/m</th>
            <th className="nowrap">Entry</th>
            <th className="nowrap">Exit</th>
            {ALL_M.map(i => <th key={i} className="num nowrap">{MONTHS[i]}-{yearSuffix}</th>)}
          </tr>
        </thead>
        <tbody>
          {peopleRows.map((p, idx) => (
            <tr key={p.id} className={idx % 2 === 0 ? 'zebra' : ''}>
              <td><strong>{(p.last_name || '').toUpperCase()}</strong> {p.first_name || ''}</td>
              <td>{p.department ? <span className="ppl-print__badge">{p.department}</span> : '—'}</td>
              <td className="num nowrap">{p.salary100 > 0 ? fmtChf(p.salary100) : '—'}</td>
              <td className="nowrap">{fmtMonthYear(p.entry_date)}</td>
              <td className="nowrap">{fmtMonthYear(p.termination_date)}</td>
              {p.cells.map((cell, i) => (
                <td key={i} className="num nowrap" style={planCellStyle(cell)}>{cell.pct}%</td>
              ))}
            </tr>
          ))}
          {syntheticRows.map(r => (
            <tr key={r.id}>
              <td><strong>{r.sign > 0 ? '+ ' : '− '}{r.label || (r.sign > 0 ? 'New position' : 'Reduction')}</strong></td>
              <td>—</td>
              <td className="num nowrap">{r.salary100 > 0 ? fmtChf(r.salary100) : '—'}</td>
              <td className="nowrap">{fmtMonthYear(r.mv.start_month)}</td>
              <td className="nowrap">{fmtMonthYear(r.mv.end_month)}</td>
              {r.cells.map((cell, i) => (
                <td key={i} className="num nowrap" style={cell.pct === 0 ? undefined : planCellStyle(cell)}>{cell.pct === 0 ? '' : `${cell.pct}%`}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="nowrap">{p('Total FTE')} <span style={{ color: '#777', fontWeight: 400, marginLeft: '4mm' }}>avg {formatNumber(avgFte, 1)}</span></td>
            {monthlyFte.map((v, i) => <td key={i} className="num nowrap">{formatNumber(v, 1)}</td>)}
          </tr>
          <tr>
            <td colSpan={5} className="nowrap">Net headcount</td>
            {monthlyHc.map((v, i) => <td key={i} className="num nowrap">{v}</td>)}
          </tr>
        </tfoot>
      </table>
    </section>
    </>
  );
}

function Kpi({ label, value, hint, accent }) {
  const cls = `ppl-print__kpi${accent ? ` ppl-print__kpi--${accent}` : ''}`;
  return (
    <div className={cls}>
      <div className="ppl-print__kpi-label">{label}</div>
      <div className="ppl-print__kpi-value">{value}</div>
      {hint && <div className="ppl-print__kpi-hint">{hint}</div>}
    </div>
  );
}
